import { makeAutoObservable, observable, runInAction } from "mobx";
import type { TProjectsBoardGroupBy, TProjectsListLayout } from "@/types/project";

const STORAGE_KEY = "projects-list-view-preferences";

/**
 * Cross-session persistence for the workspace Projects list view (Table /
 * Board / Timeline / Calendar).
 *
 * Mirrors {@link WorkItemViewPreferencesStore}: the in-page source of truth
 * stays the URL (``?layout=…``); this store only remembers a user's last-used
 * layout so a fresh visit with no layout in the URL (sidebar navigation, a
 * bookmark, a new session) can restore it. ``localStorage`` is the right
 * backend — a last-used layout is a client-only UI preference, not server
 * state — and keeping the read/write in a store (rather than poking
 * ``localStorage`` from the component) gives us SSR-safety + error handling in
 * one place.
 *
 * Keyed by workspace slug: the Projects list is workspace-scoped (unlike the
 * per-project work-items view, which keys by project id).
 */
export class ProjectsListViewPreferencesStore {
  layoutByWorkspace = observable.map<string, TProjectsListLayout>();
  /** What the Board makes its columns out of, and whose stages those are
   *  (NG-713). Persisted for the same reason the layout is: the pick lives in
   *  `ProjectStore` while the page is open, so without this a reload dropped a
   *  stage board back to Status and the lanes read as broken. */
  boardGroupByByWorkspace = observable.map<string, TProjectsBoardGroupBy>();
  boardProcessByWorkspace = observable.map<string, string>();

  constructor() {
    makeAutoObservable(this, {} as never, { autoBind: true });
    this.hydrate();
  }

  private hydrate(): void {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as {
        layoutByWorkspace?: Record<string, TProjectsListLayout>;
        boardGroupByByWorkspace?: Record<string, TProjectsBoardGroupBy>;
        boardProcessByWorkspace?: Record<string, string>;
      };
      runInAction(() => {
        this.layoutByWorkspace.replace(parsed.layoutByWorkspace ?? {});
        // Absent for anyone whose prefs predate NG-713 — an empty map, not a
        // reset, so their stored layout survives the upgrade.
        this.boardGroupByByWorkspace.replace(parsed.boardGroupByByWorkspace ?? {});
        this.boardProcessByWorkspace.replace(parsed.boardProcessByWorkspace ?? {});
      });
    } catch (err) {
      // Corrupt or unreadable (private mode / quota) — start clean.
      console.warn("[projects-list-prefs] hydrate failed; resetting", err);
      runInAction(() => {
        this.layoutByWorkspace.clear();
        this.boardGroupByByWorkspace.clear();
        this.boardProcessByWorkspace.clear();
      });
    }
  }

  private persist(): void {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          layoutByWorkspace: Object.fromEntries(this.layoutByWorkspace),
          boardGroupByByWorkspace: Object.fromEntries(this.boardGroupByByWorkspace),
          boardProcessByWorkspace: Object.fromEntries(this.boardProcessByWorkspace),
        }),
      );
    } catch {
      // Best-effort: localStorage can throw in private mode / on quota.
    }
  }

  getLayout(workspaceSlug: string): TProjectsListLayout | null {
    return this.layoutByWorkspace.get(workspaceSlug) ?? null;
  }

  setLayout(workspaceSlug: string, layout: TProjectsListLayout): void {
    if (this.layoutByWorkspace.get(workspaceSlug) === layout) return;
    this.layoutByWorkspace.set(workspaceSlug, layout);
    this.persist();
  }

  getBoardGroupBy(workspaceSlug: string): TProjectsBoardGroupBy | null {
    return this.boardGroupByByWorkspace.get(workspaceSlug) ?? null;
  }

  setBoardGroupBy(workspaceSlug: string, groupBy: TProjectsBoardGroupBy): void {
    if (this.boardGroupByByWorkspace.get(workspaceSlug) === groupBy) return;
    this.boardGroupByByWorkspace.set(workspaceSlug, groupBy);
    this.persist();
  }

  getBoardProcessId(workspaceSlug: string): string | null {
    return this.boardProcessByWorkspace.get(workspaceSlug) ?? null;
  }

  setBoardProcessId(workspaceSlug: string, processId: string): void {
    if (this.boardProcessByWorkspace.get(workspaceSlug) === processId) return;
    this.boardProcessByWorkspace.set(workspaceSlug, processId);
    this.persist();
  }
}
